import { useEffect, useState } from "react";
import { FileSpreadsheet, Upload, Check, AlertTriangle, History, Percent, Save, ArrowRight, Info, PackagePlus, Download, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatARS, formatApiError } from "@/lib/api";
import { SELLERS } from "@/lib/sellers";

const BIG_JUMP_PCT = 30;
const CATEGORIES = ["Limpieza", "Bazar", "Perfumería", "Ofertas"];

export default function PricesTab({ onApplied }) {
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/prices/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const revert = async (h) => {
    const parts = [];
    if (h.updated) parts.push(`volver ${h.updated} precio(s) a su valor anterior`);
    if (h.created) parts.push(`eliminar los ${h.created} producto(s) creados`);
    if (!window.confirm(`Se va a ${parts.join(" y ")} de "${h.filename || "lista de precios"}". ¿Confirmás?`)) return;
    try {
      const { data } = await api.post(`/prices/history/${h.id}/revert`);
      toast.success(`Actualización revertida: ${data.updated} precio(s) restaurados${data.deleted ? ` · ${data.deleted} producto(s) eliminados` : ""}`);
      loadHistory();
      onApplied?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo revertir");
    }
  };

  return (
    <div className="space-y-8" data-testid="prices-tab">
      <PriceUpdater onApplied={() => { loadHistory(); onApplied?.(); }} />
      <PriceListsSettings />
      <PriceHistory items={history} onRevert={revert} />
    </div>
  );
}

function PriceUpdater({ onApplied }) {
  const [stage, setStage] = useState("upload"); // upload | loading | preview | applying
  const [result, setResult] = useState(null);
  const [view, setView] = useState("matched");
  const [excluded, setExcluded] = useState({});
  const [creating, setCreating] = useState({});
  const [createCategory, setCreateCategory] = useState("Bazar");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStage("loading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/prices/parse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(data);
      setExcluded({});
      setCreating({});
      setView(data.matched.length ? "matched" : data.not_found.length ? "not_found" : "unchanged");
      setStage("preview");
      toast.success(`Lista leída: ${data.rows_total} filas, ${data.matched.length} cambios detectados`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al leer el archivo");
      setStage("upload");
    } finally {
      e.target.value = "";
    }
  };

  const reset = () => { setStage("upload"); setResult(null); setExcluded({}); setCreating({}); };
  const toApply = result ? result.matched.filter((m) => !excluded[m.product_id]) : [];
  const toCreate = result ? result.not_found.filter((r) => creating[r.code]) : [];
  const bigJumps = result ? result.matched.filter((m) => m.pct !== null && Math.abs(m.pct) > BIG_JUMP_PCT) : [];
  const bigUps = bigJumps.filter((m) => m.pct > 0).length;
  const bigDowns = bigJumps.length - bigUps;

  const apply = async () => {
    if (!toApply.length && !toCreate.length) { toast.error("No hay cambios seleccionados"); return; }
    const parts = [];
    if (toApply.length) parts.push(`actualizar ${toApply.length} precio(s)`);
    if (toCreate.length) parts.push(`crear ${toCreate.length} producto(s) nuevo(s) en "${createCategory}"`);
    if (!window.confirm(`Se van a ${parts.join(" y ")}. ¿Confirmás?`)) return;
    setStage("applying");
    try {
      const { data } = await api.post("/prices/apply", {
        updates: toApply.map((m) => ({ product_id: m.product_id, price: m.new_price })),
        create: toCreate.map((r) => ({ code: r.code, description: r.description, price: r.price })),
        create_category: createCategory,
        filename: result.filename, not_found: result.not_found.length - toCreate.length, source: result.source,
      });
      toast.success(`${data.updated} precio(s) actualizados${data.created ? ` · ${data.created} producto(s) creados` : ""}`);
      reset();
      onApplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al aplicar los precios");
      setStage("preview");
    }
  };

  const applyLabel = [toApply.length ? `${toApply.length} cambio${toApply.length === 1 ? "" : "s"}` : "", toCreate.length ? `crear ${toCreate.length}` : ""].filter(Boolean).join(" · ") || "0 cambios";

  const [exporting, setExporting] = useState(false);
  const exportCurrent = async () => {
    setExporting(true);
    try {
      const res = await api.get("/prices/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `produlimp-lista-precios-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Lista de precios descargada");
    } catch {
      toast.error("No se pudo exportar la lista");
    } finally { setExporting(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="price-updater">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-600" /> Actualizar lista de precios</h2>
          <p className="text-xs text-slate-500 mt-1">Subí tu lista (Excel, CSV o PDF) con columnas <strong>Código, Descripción y Precio final</strong>. Los productos se identifican por su código (SKU).</p>
        </div>
        <button onClick={exportCurrent} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-green-700 border-2 border-green-200 hover:bg-green-50 transition-colors disabled:opacity-50" data-testid="export-price-list-btn">
          <Download className="w-4 h-4" /> {exporting ? "Exportando..." : "Exportar lista actual (Excel)"}
        </button>
      </div>

      {stage === "upload" && (
        <div className="p-6">
          <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl py-12 cursor-pointer hover:border-green-400 hover:bg-green-50/30 transition-colors" data-testid="price-dropzone">
            <Upload className="w-10 h-10 text-green-600" />
            <span className="text-base font-semibold text-slate-700">Subí la lista de precios actualizada</span>
            <span className="text-xs text-slate-400">Excel (.xlsx), CSV o PDF — hasta 30 MB</span>
            <input type="file" accept=".xlsx,.xlsm,.csv,.txt,.pdf,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onFile} data-testid="price-file-input" />
          </label>
          <ul className="text-xs text-slate-500 mt-4 space-y-1 list-disc pl-5">
            <li>Vas a ver una vista previa con los cambios antes de aplicarlos</li>
            <li>Las subas o bajas mayores al {BIG_JUMP_PCT}% se marcan para detectar errores de tipeo</li>
            <li>Los códigos que no existan se informan y podés elegir crearlos como productos nuevos</li>
            <li>Si el PDF no tiene texto legible, se usa IA para leerlo</li>
          </ul>
        </div>
      )}

      {(stage === "loading" || stage === "applying") && (
        <div className="p-12 text-center">
          <div className="inline-block w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
          <p className="mt-4 text-slate-600">{stage === "loading" ? "Leyendo la lista de precios..." : "Aplicando precios..."}</p>
        </div>
      )}

      {stage === "preview" && result && (
        <>
          {bigJumps.length > 0 && (
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center gap-2 text-sm text-amber-900" data-testid="big-jump-alert">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span>
                {bigUps > 0 && <><strong>{bigUps} precio{bigUps === 1 ? "" : "s"}</strong> sube{bigUps === 1 ? "" : "n"} más del {BIG_JUMP_PCT}%</>}
                {bigUps > 0 && bigDowns > 0 && " y "}
                {bigDowns > 0 && <><strong>{bigDowns} precio{bigDowns === 1 ? "" : "s"}</strong> baja{bigDowns === 1 ? "" : "n"} más del {BIG_JUMP_PCT}%</>}
                . Revisá que no sean errores de tipeo antes de aplicar.
              </span>
              <button onClick={() => setView("big")} className="ml-auto text-xs font-semibold underline underline-offset-2 hover:text-amber-700" data-testid="big-jump-view-btn">Ver solo estos</button>
            </div>
          )}
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-600 mr-2 truncate max-w-[220px]" title={result.filename}>{result.filename}</span>
            <FilterPill active={view === "matched"} onClick={() => setView("matched")} color="green" testid="view-matched">Cambios ({result.matched.length})</FilterPill>
            {bigJumps.length > 0 && <FilterPill active={view === "big"} onClick={() => setView("big")} color="amber" testid="view-big">Variaciones &gt;{BIG_JUMP_PCT}% ({bigJumps.length})</FilterPill>}
            <FilterPill active={view === "unchanged"} onClick={() => setView("unchanged")} color="slate" testid="view-unchanged">Sin cambios ({result.unchanged.length})</FilterPill>
            <FilterPill active={view === "not_found"} onClick={() => setView("not_found")} color="blue" testid="view-not-found">No encontrados ({result.not_found.length})</FilterPill>
            {result.products_without_sku > 0 && <span className="text-xs text-slate-400 ml-auto">{result.products_without_sku} producto(s) de la tienda sin código</span>}
          </div>
          <div className="max-h-[420px] overflow-auto">
            {view === "matched" && <MatchedTable rows={result.matched} excluded={excluded} setExcluded={setExcluded} />}
            {view === "big" && <MatchedTable rows={bigJumps} excluded={excluded} setExcluded={setExcluded} />}
            {view === "unchanged" && <SimpleTable rows={result.unchanged.map((r) => ({ a: r.sku, b: r.name, c: formatARS(r.old_price) }))} headers={["Código", "Producto", "Precio"]} empty="Todos los precios cambiaron." />}
            {view === "not_found" && <NotFoundTable rows={result.not_found} creating={creating} setCreating={setCreating} category={createCategory} setCategory={setCreateCategory} />}
          </div>
          <div className="p-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
            {toCreate.length > 0 && <span className="text-xs text-blue-700 mr-auto flex items-center gap-1"><PackagePlus className="w-3.5 h-3.5" /> {toCreate.length} producto(s) nuevo(s) en "{createCategory}"</span>}
            <button onClick={reset} className="px-4 py-2 rounded-lg hover:bg-slate-100" data-testid="price-cancel-btn">Cancelar</button>
            <button onClick={apply} disabled={!toApply.length && !toCreate.length} className="btn-primary" data-testid="price-apply-btn">
              <Check className="w-4 h-4" /> Aplicar {applyLabel}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const FilterPill = ({ active, onClick, color, children, testid }) => {
  const palette = {
    green: active ? "bg-green-600 text-white border-green-600" : "text-green-700 border-green-200 hover:bg-green-50",
    slate: active ? "bg-slate-700 text-white border-slate-700" : "text-slate-600 border-slate-200 hover:bg-slate-100",
    amber: active ? "bg-amber-500 text-white border-amber-500" : "text-amber-700 border-amber-200 hover:bg-amber-50",
    blue: active ? "bg-blue-600 text-white border-blue-600" : "text-blue-700 border-blue-200 hover:bg-blue-50",
  };
  return <button onClick={onClick} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${palette[color]}`} data-testid={testid}>{children}</button>;
};

function MatchedTable({ rows, excluded, setExcluded }) {
  if (!rows.length) return <p className="p-10 text-center text-slate-500 text-sm">No hay cambios de precio en esta lista.</p>;
  const allSelected = rows.every((r) => !excluded[r.product_id]);
  const toggleAll = (checked) => {
    const ex = { ...excluded };
    rows.forEach((r) => { if (checked) delete ex[r.product_id]; else ex[r.product_id] = true; });
    setExcluded(ex);
  };
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b sticky top-0">
        <tr>
          <th className="p-2 w-10"><input type="checkbox" checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} data-testid="price-select-all" /></th>
          <th className="text-left p-2 w-28">Código</th>
          <th className="text-left p-2">Producto</th>
          <th className="text-right p-2 w-28">Actual</th>
          <th className="w-8"></th>
          <th className="text-right p-2 w-28">Nuevo</th>
          <th className="text-right p-2 w-24">Var.</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const big = r.pct !== null && Math.abs(r.pct) > BIG_JUMP_PCT;
          return (
            <tr key={r.product_id} className={`border-b border-slate-100 ${excluded[r.product_id] ? "opacity-40" : ""} ${big ? "bg-amber-50/60" : ""}`} data-testid={`price-row-${r.product_id}`}>
              <td className="p-2 text-center"><input type="checkbox" checked={!excluded[r.product_id]} onChange={(e) => setExcluded({ ...excluded, [r.product_id]: !e.target.checked })} /></td>
              <td className="p-2 font-mono text-xs text-blue-700">{r.sku}</td>
              <td className="p-2"><div className="font-medium truncate max-w-[280px]">{r.name}</div>{r.description && r.description !== r.name && <div className="text-[11px] text-slate-400 truncate max-w-[280px]">{r.description}</div>}</td>
              <td className="p-2 text-right text-slate-500">{formatARS(r.old_price)}</td>
              <td className="text-center text-slate-300"><ArrowRight className="w-3.5 h-3.5 inline" /></td>
              <td className="p-2 text-right font-semibold">{formatARS(r.new_price)}</td>
              <td className={`p-2 text-right text-xs font-semibold ${r.pct > 0 ? "text-red-600" : "text-green-600"}`}>
                <span className="inline-flex items-center gap-1 justify-end">
                  {big && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" data-testid={`big-jump-${r.product_id}`} />}
                  {r.pct === null ? "—" : `${r.pct > 0 ? "+" : ""}${r.pct}%`}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SimpleTable({ rows, headers, empty }) {
  if (!rows.length) return <p className="p-10 text-center text-slate-500 text-sm">{empty}</p>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b sticky top-0">
        <tr>{headers.map((h) => <th key={h} className={`p-2 ${h.startsWith("Precio") ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="p-2 font-mono text-xs">{r.a}</td>
            <td className="p-2 truncate max-w-[300px]">{r.b}</td>
            <td className="p-2 text-right">{r.c}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NotFoundTable({ rows, creating, setCreating, category, setCategory }) {
  if (!rows.length) return <p className="p-10 text-center text-slate-500 text-sm">Todos los códigos existen en la tienda.</p>;
  const selected = rows.filter((r) => creating[r.code]).length;
  const toggleAll = (checked) => { const c = {}; if (checked) rows.forEach((r) => { c[r.code] = true; }); setCreating(c); };
  return (
    <>
      <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-xs text-blue-900 flex flex-wrap items-center gap-3">
        <PackagePlus className="w-4 h-4 text-blue-600" />
        <span>Marcá los códigos que quieras <strong>crear como productos nuevos</strong> (nombre = descripción, stock 10, sin imagen).</span>
        <label className="ml-auto flex items-center gap-2">
          Categoría:
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1 rounded-lg border border-blue-200 bg-white text-slate-800 outline-none" data-testid="create-category-select">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <span className="font-semibold">{selected} seleccionado(s)</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b sticky top-0">
          <tr>
            <th className="p-2 w-10"><input type="checkbox" checked={selected === rows.length} onChange={(e) => toggleAll(e.target.checked)} data-testid="create-select-all" /></th>
            <th className="text-left p-2 w-28">Código</th>
            <th className="text-left p-2">Descripción (lista)</th>
            <th className="text-right p-2 w-32">Precio (lista)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className={`border-b border-slate-100 ${creating[r.code] ? "bg-blue-50/40" : ""}`} data-testid={`not-found-row-${r.code}`}>
              <td className="p-2 text-center"><input type="checkbox" checked={!!creating[r.code]} onChange={(e) => setCreating({ ...creating, [r.code]: e.target.checked })} data-testid={`create-check-${r.code}`} /></td>
              <td className="p-2 font-mono text-xs">{r.code}</td>
              <td className="p-2 truncate max-w-[300px]">{r.description || <span className="text-slate-400 italic">sin descripción (se usará el código)</span>}</td>
              <td className="p-2 text-right">{formatARS(r.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function PriceListsSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/prices/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings) return <div className="text-center py-8 text-slate-500">Cargando listas...</div>;

  const pctOf = (n) => settings.lists.find((l) => l.n === n)?.pct ?? 0;
  const setPct = (n, pct) => { setSettings({ ...settings, lists: settings.lists.map((l) => (l.n === n ? { ...l, pct } : l)) }); setDirty(true); };
  const setSeller = (code, n) => { setSettings({ ...settings, sellers: { ...settings.sellers, [code]: n } }); setDirty(true); };
  const allBase = SELLERS.every((s) => !(settings.sellers[s.code] ?? 0));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/prices/settings", settings);
      setSettings(data);
      setDirty(false);
      toast.success("Listas de precios guardadas");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="price-lists-settings">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Percent className="w-5 h-5 text-blue-600" /> Listas de precios por vendedor</h2>
          <p className="text-xs text-slate-500 mt-1">El precio de la lista es el precio base. Cada vendedor puede tener un recargo según la lista asignada; sus clientes ven los precios ya ajustados.</p>
        </div>
        <button onClick={save} disabled={!dirty || saving} className="btn-primary" data-testid="price-settings-save">
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
      {allBase && !dirty && (
        <div className="px-5 py-2.5 bg-blue-50 border-b border-blue-100 text-xs text-blue-900 flex items-center gap-2" data-testid="all-base-hint">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" /> Todos los vendedores están en <strong>precio base</strong>. Asigná una lista a cada uno y guardá para empezar a aplicar los recargos.
        </div>
      )}
      <div className="grid md:grid-cols-[260px_1fr] divide-y md:divide-y-0 md:divide-x divide-slate-100">
        <div className="p-5 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recargo por lista</div>
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex items-center gap-3" data-testid={`price-list-${n}`}>
              <span className="w-16 text-sm font-medium text-slate-700">Lista {n}</span>
              <div className="flex items-center gap-1">
                <span className="text-slate-400 text-sm">+</span>
                <input type="number" min="0" max="500" step="0.5" value={pctOf(n)} onChange={(e) => setPct(n, parseFloat(e.target.value) || 0)} className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-400 text-right font-semibold" data-testid={`price-list-pct-${n}`} />
                <span className="font-semibold text-sm">%</span>
              </div>
            </div>
          ))}
        </div>
        <div className="divide-y divide-slate-100">
          {SELLERS.map((s) => {
            const n = settings.sellers[s.code] ?? 0;
            return (
              <div key={s.code} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`seller-price-list-${s.code}`}>
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-mono font-bold w-10 text-center">{s.code}</span>
                <span className="font-medium text-slate-900 flex-1 min-w-[140px]">{s.name}</span>
                <select value={n} onChange={(e) => setSeller(s.code, parseInt(e.target.value))} className="px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-400 text-sm bg-white" data-testid={`seller-price-list-select-${s.code}`}>
                  <option value={0}>Precio base (0%)</option>
                  {[1, 2, 3, 4].map((k) => <option key={k} value={k}>{`Lista ${k} (+${pctOf(k)}%)`}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PriceHistory({ items, onRevert }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="price-history">
      <div className="px-5 py-4 border-b border-slate-200">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><History className="w-5 h-5 text-slate-500" /> Historial de actualizaciones</h2>
        <p className="text-xs text-slate-500 mt-1">Podés deshacer una actualización: los precios vuelven a su valor anterior y se eliminan los productos que haya creado.</p>
      </div>
      {items.length === 0 ? (
        <p className="p-8 text-center text-slate-500 text-sm">Todavía no se aplicaron actualizaciones de precios.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((h) => {
            const isRevert = h.source === "revert";
            const canRevert = !isRevert && !h.reverted_at && (h.updated > 0 || h.created > 0);
            return (
              <div key={h.id} className={`px-5 py-3 flex flex-wrap items-center gap-3 text-sm ${h.reverted_at ? "opacity-60" : ""}`} data-testid={`price-history-${h.id}`}>
                <span className="text-slate-500 text-xs w-36">{new Date(h.created_at).toLocaleString("es-AR")}</span>
                <span className={`font-medium flex-1 truncate ${isRevert ? "text-slate-600 italic" : ""}`}>{isRevert && <Undo2 className="w-3.5 h-3.5 inline mr-1 text-slate-400" />}{h.filename || "Lista de precios"}</span>
                <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">{h.updated} {isRevert ? "restaurados" : "actualizados"}</span>
                {h.created > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">{h.created} creados</span>}
                {h.deleted > 0 && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">{h.deleted} eliminados</span>}
                {h.not_found > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">{h.not_found} no encontrados</span>}
                {h.reverted_at && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold" data-testid={`price-history-reverted-${h.id}`}>Revertida {new Date(h.reverted_at).toLocaleDateString("es-AR")}</span>}
                {canRevert && (
                  <button onClick={() => onRevert(h)} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors" data-testid={`price-history-revert-${h.id}`}>
                    <Undo2 className="w-3.5 h-3.5" /> Deshacer
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
